import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  UniversalPage,
  UniversalTable,
  StatusBadge,
  type StatusTone,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type UniversalTableColumn,
  PeriodFilter,
  SearchField,
  TABLE_PRESETS,
} from '@sighthost/ui';
import { Package } from 'lucide-react';
import {
  adminServiceOrdersService,
  CLOSED_SERVICE_ORDER_STATUSES,
  type ServiceOrderListRow,
} from '@/domains/services/services/adminServiceOrdersService';
import { formatZARFromCents } from '@/shared/utils/format';

/**
 * AdminServiceOrdersPage — the service-order worklist. AR-10.
 *
 * Until this shipped there was no `/admin/services`: the detail route
 * `/admin/services/:id` existed with its list living at `/admin/reports/services`,
 * so the only path to a live R3,990 Studio order ran through a report, and no
 * detail page could derive a breadcrumb because its parent sat under a
 * different path prefix.
 *
 * The report keeps the aggregates. This is the surface you work from.
 */


/** Service-order status -> shared StatusBadge tone (which is a SEMANTIC scale,
 *  not a colour name: `active`/`paid` are green, `pending`/`expiring` amber,
 *  `draft` grey, `info` blue).
 *
 *  `paid` maps to AMBER, not green. Money has arrived and nothing has been
 *  delivered yet — that is the state most in need of a human, and green would
 *  read as "done". The mismatch between the DB status called `paid` and the
 *  badge tone called `expiring` is deliberate; the tone describes urgency, the
 *  status describes the record. */
const STATUS_TONE: Record<string, StatusTone> = {
  pending_payment: 'draft',
  paid: 'expiring',
  awaiting_consultation: 'expiring',
  in_progress: 'info',
  complete: 'active',
  cancelled: 'draft',
};

function label(status: string): string {
  return status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

const OPEN = '__open__';
const ALL = '__all__';
const UNASSIGNED = '__unassigned__';

export default function AdminServiceOrdersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ServiceOrderListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Opens on OPEN, not ALL. A worklist whose default view is "everything ever"
  // makes the reader do the filtering the page exists to do.
  const [status, setStatus] = useState<string>(OPEN);
  const [sku, setSku] = useState<string>(ALL);
  const [assignee, setAssignee] = useState<string>(ALL);

  useEffect(() => {
    let cancelled = false;
    adminServiceOrdersService
      .list()
      .then((data) => {
        if (!cancelled) {
          setRows(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Options come from the DATA, not a hardcoded list. A new SKU or a new status
  // appears in the filter the day it appears in the table — the alternative is
  // a filter that silently cannot reach the newest rows.
  const skuOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.sku_code))).sort(),
    [rows],
  );
  const statusOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.status))).sort(),
    [rows],
  );

  /**
   * Service orders are FLOW — the period is meaningful, labelled `Created`.
   * AR-5 option (a): 56 orders against a 200 cap (measured 2026-09-13), so the
   * fetched set is complete.
   */
  const table = useTableFilters<ServiceOrderListRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Created',
    dateOf: (r) => r.created_at,
    searchText: (r) =>
      [r.sku_code, r.customer_name ?? '', r.customer_email ?? '', r.status].join(' '),
    facets: useMemo(
      () => [
        (r: ServiceOrderListRow) =>
          status === ALL ||
          (status === OPEN
            ? !(CLOSED_SERVICE_ORDER_STATUSES as readonly string[]).includes(r.status)
            : r.status === status),
        (r: ServiceOrderListRow) => sku === ALL || r.sku_code === sku,
        (r: ServiceOrderListRow) => assignee !== UNASSIGNED || !r.assignee_user_id,
      ],
      [status, sku, assignee],
    ),
  });

  const filtered = table.rows;

  const columns = useMemo<UniversalTableColumn<ServiceOrderListRow>[]>(
    () => [
      {
        key: 'customer',
        header: 'Customer',
        sortValue: (r) => r.customer_name ?? r.customer_email ?? '',
        render: (r) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">
              {r.customer_name ?? r.customer_email ?? '—'}
            </p>
            {r.primary_fqdn && (
              <p className="truncate text-xs text-muted-foreground">{r.primary_fqdn}</p>
            )}
          </div>
        ),
      },
      {
        key: 'sku',
        header: 'Product',
        sortValue: (r) => r.sku_code,
        render: (r) => <code className="text-sm text-foreground">{r.sku_code}</code>,
        width: 'w-40',
      },
      {
        key: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        render: (r) => (
          <StatusBadge tone={STATUS_TONE[r.status] ?? 'draft'}>{label(r.status)}</StatusBadge>
        ),
        width: 'w-44',
      },
      {
        key: 'assignee',
        header: 'Owner',
        sortValue: (r) => r.assignee_name ?? '',
        render: (r) =>
          r.assignee_name ? (
            <span className="text-sm text-foreground">{r.assignee_name}</span>
          ) : (
            <span className="text-sm text-warning">Unassigned</span>
          ),
        width: 'w-40',
      },
      {
        key: 'paid',
        header: 'Paid',
        align: 'right',
        sortValue: (r) => r.total_paid_cents,
        render: (r) => (
          <span className="font-semibold text-foreground">{formatZARFromCents(r.total_paid_cents)}</span>
        ),
        width: 'w-32',
      },
      {
        key: 'age',
        header: 'Age',
        align: 'right',
        sortValue: (r) => new Date(r.created_at).getTime(),
        render: (r) => {
          const d = daysSince(r.created_at);
          return (
            <span className="text-sm text-muted-foreground">
              {d === 0 ? 'Today' : `${d}d`}
            </span>
          );
        },
        width: 'w-24',
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Service orders"
      description="Studio, Care and one-off product orders. Open the row to work it."
      breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Services' }]}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={OPEN}>Open orders</SelectItem>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {label(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sku} onValueChange={setSku}>
            <SelectTrigger className="w-40" aria-label="Filter by product">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All products</SelectItem>
              {skuOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger className="w-40" aria-label="Filter by owner">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any owner</SelectItem>
              <SelectItem value={UNASSIGNED}>Unassigned only</SelectItem>
            </SelectContent>
          </Select>
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Created"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search product, customer, status"
            className="w-full max-w-xs"
          />
        </div>
      }
    >
      <UniversalTable<ServiceOrderListRow>
        data={filtered}
        columns={columns}
        getRowKey={(r) => r.id}
        caption={table.caption}
        onRowClick={(r) => navigate(`/admin/services/${r.id}`)}
        isLoading={loading}
        error={error}
        empty={{
          icon: Package,
          title: status === OPEN ? 'No open service orders' : 'No service orders match',
          description:
            status === OPEN
              ? 'Everything is complete or cancelled. Switch to "All statuses" to see history.'
              : 'Try widening the filters above.',
        }}
      />
    </UniversalPage>
  );
}
